import React from 'react';
import { useDsTokens } from '../theme/useDsTokens';
import { DsResolvedTokens } from '../theme/tokens';
import { cn } from '../utils/cn';
import { BadgeVariant, DsPrimitiveProps } from './types';

export interface BadgeProps extends DsPrimitiveProps {
  variant?: BadgeVariant;
  badgeContent?: number;
  invisible?: boolean;
  max?: number;
  children: React.ReactElement;
}

function formatContent(content: number | undefined, max: number): string | null {
  if (content === undefined) return null;
  if (content > max) return `${max}+`;
  return String(content);
}

function badgeColors(variant: BadgeVariant, tokens: DsResolvedTokens) {
  let bg = tokens.statusError;
  if (variant === 'dot') bg = tokens.brandPrimary;
  if (variant === 'status-success') bg = tokens.statusSuccess;
  if (variant === 'status-warning') bg = tokens.statusWarning;
  if (variant === 'status-error') bg = tokens.statusError;
  return bg;
}

export function Badge({
  id,
  className,
  variant = 'standard',
  badgeContent,
  invisible,
  max = 99,
  children,
  'data-testid': testId,
}: BadgeProps) {
  const tokens = useDsTokens();
  const isDot = variant === 'dot';

  const showInvisible =
    invisible ||
    (variant === 'standard' && badgeContent === 0);

  const displayContent = isDot ? null : formatContent(badgeContent, max);

  return (
    <span
      id={id}
      className={cn(className)}
      data-testid={testId}
      style={{
        position: 'relative',
        display: 'inline-flex',
        verticalAlign: 'middle',
      }}
    >
      {children}
      {!showInvisible && (
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            transform: 'translate(50%, -50%)',
            transformOrigin: '100% 0%',
            backgroundColor: badgeColors(variant, tokens),
            color: tokens.onPrimary,
            fontSize: tokens.fontSizeCaption,
            fontWeight: tokens.fontWeightMedium,
            lineHeight: 1,
            minWidth: isDot ? tokens.space1 : tokens.space1 + tokens.spaceHalf,
            height: isDot ? tokens.space1 : tokens.space1 + tokens.spaceHalf,
            padding: isDot ? 0 : `0 ${tokens.spaceHalf}px`,
            borderRadius: tokens.radiusFull,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: `opacity ${tokens.motionDurationMicro}ms ${tokens.motionEasingStandard}`,
            pointerEvents: 'none',
          }}
        >
          {displayContent}
        </span>
      )}
    </span>
  );
}

Badge.displayName = 'Badge';
